<script setup lang="ts">
import ProjectSuggestionDetailData = App.Data.Bank.ProjectSuggestionDetailData
import ProjectSuggestionStatus = App.Enums.Project.ProjectSuggestionStatus
import Borrower from '@/pages/banks/financing-request/Borrower.vue'
import FinancingObjects from '@/pages/banks/financing-request/FinancingObjects.vue'
import { isProjectSensitiveKey, sensitiveFieldsKey } from '@/utils/sensitive-fields'
import ProjectsController from '@controllers/Banks/ProjectsController'
import { router } from '@inertiajs/vue3'
import type { SelectButtonPassThroughOptions } from 'primevue/selectbutton'
import { computed, provide, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    project: ProjectSuggestionDetailData
    sensitive?: string[]
  }>(),
  {
    sensitive: () => [
      'AttachmentData.filename',
      'AttachmentData.description',
      'AttachmentData.path',
      'AttachmentData.uploaded_by',
      'CompanyData.name',
      'CompanyData.address',
      'CompanyData.managing_director',
      'EstateObjectData.address',
      'NaturalPersonData.first_name',
      'NaturalPersonData.last_name',
      'NaturalPersonData.name',
      'NaturalPersonData.email',
      'NaturalPersonData.phone',
      'NaturalPersonData.address',
    ],
  },
)

const projectTitle = computed(() => {
  const objectTypes = [
    ...new Set(
      props.project.estate_objects
        .map((estateObject) => estateObject.estate_object_type)
        .filter(Boolean)
        .map((type) => t(`enums.estateObjectType.${type}`)),
    ),
  ]
  const cities = [...new Set(props.project.estate_objects.map((estateObject) => estateObject.city).filter(Boolean))]

  if (!objectTypes.length && !cities.length) {
    return ''
  }

  return [objectTypes.join(', '), cities.join(', ')].filter(Boolean).join(' · ')
})

const projectStatusLabel = computed(() => t(`enums.projectSuggestionStateShort.${props.project.status}`))

const View = {
  Objects: 'objects',
  Borrower: 'borrower',
} as const

const selectedView = ref<(typeof View)[keyof typeof View]>(View.Objects)
const viewOptions = computed(() => [
  {
    label: props.project.estate_objects.length === 1 ? t('financing_object') : t('financing_objects'),
    value: View.Objects,
    icon: 'pi pi-building',
  },
  {
    label: t('borrower'),
    value: View.Borrower,
    icon: 'pi pi-users',
  },
])

const projectViewSelectPt: SelectButtonPassThroughOptions = {
  root: {
    class:
      'shadow-card flex w-full overflow-hidden !rounded-2xl gap-2 p-2 border border-surface-200 bg-surface-0 min-h-20',
  },
  pcToggleButton: {
    root: ({ context }: { context: { active: boolean } }) => ({
      class: [
        'flex-1 !border-0 !rounded-xl',
        !context.active && '!bg-white !text-surface-500 hover:!bg-gray-200',
        context.active && '!bg-success-800 !text-white',
      ],
    }),
    content: () => ({
      class: ['!bg-transparent !shadow-none h-full w-full'],
    }),
  },
}

const actionButtons = computed(() => {
  const status = props.project.status

  return [
    {
      label: t('actions.dismiss'),
      severity: 'danger',
      show: status === 'created',
      onClick: () => updateProjectSuggestionStatus('cancelled'),
    },
    {
      label: t('actions.withdrawProposal'),
      severity: 'danger',
      show: status === 'proposed',
      onClick: () => updateProjectSuggestionStatus('cancelled'),
    },
    {
      label: t('actions.cancelEngagement'),
      severity: 'danger',
      show: status === 'engaged',
      onClick: () => updateProjectSuggestionStatus('cancelled'),
    },
    {
      label: t('actions.proposeEngagement'),
      severity: 'success',
      show: status === 'created',
      onClick: () => updateProjectSuggestionStatus('proposed'),
    },
  ].filter((button) => button.show)
})

const updateProjectSuggestionStatus = (status: ProjectSuggestionStatus) => {
  router.put(
    ProjectsController.update(props.project.id),
    { status },
    {
      preserveScroll: true,
    },
  )
}

const isProjectSensitive = computed(() => {
  return props.project.status === 'created'
})

provide(
  sensitiveFieldsKey,
  computed(() => new Set(props.sensitive)),
)

provide(isProjectSensitiveKey, isProjectSensitive)
</script>

<template>
  <div class="flex flex-col gap-6">
    <div class="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
      <div class="flex flex-col gap-2">
        <h1 v-if="projectTitle" class="text-surface-900 text-2xl font-semibold">
          {{ projectTitle }}
        </h1>
        <div class="flex flex-row items-center gap-4">
          <Tag :value="projectStatusLabel" severity="success" class="w-fit" />
          <div class="flex items-baseline gap-3">
            <span class="text-surface-500 text-sm uppercase">{{ t('financing_due_date') }}</span>
            <span class="text-surface-950">{{ project.project.financing_due_date || '-' }}</span>
          </div>
        </div>
      </div>

      <div class="flex flex-wrap gap-3">
        <Button
          v-for="button in actionButtons"
          :key="button.label"
          :label="button.label"
          :severity="button.severity"
          :outlined="button.severity === 'danger'"
          class="w-50"
          @click="button.onClick" />
      </div>
    </div>

    <SelectButton
      v-model="selectedView"
      :options="viewOptions"
      option-label="label"
      option-value="value"
      :allow-empty="false"
      class="w-full"
      :pt="projectViewSelectPt">
      <template #option="{ option }">
        <span class="flex items-center justify-center gap-3">
          <i :class="option.icon" aria-hidden="true" />
          <span>{{ option.label }}</span>
          <span
            v-if="option.value === View.Objects && project.estate_objects.length > 1"
            class="bg-success-800 inline-flex size-7 items-center justify-center rounded-full text-sm font-semibold text-white">
            {{ project.estate_objects.length }}
          </span>
        </span>
      </template>
    </SelectButton>

    <FinancingObjects
      v-if="selectedView === View.Objects"
      :estate-objects="project.estate_objects"
      :project="project.project"
      :attachments="project.attachments" />
    <Borrower v-else :project="project" />
  </div>
</template>

<i18n>
{
    "en": {
        "actions": {
            "proposeEngagement": "Propose Engagement",
            "dismiss": "Dismiss",
            "withdrawProposal": "Withdraw Proposal",
            "cancelEngagement": "Cancel Engagement"
        },
        "financing_object": "Financing Object",
        "financing_objects": "Financing Objects",
        "borrower": "Borrower",
        "financing_due_date": "Financing Due Date"
    },
    "de": {
        "actions": {
            "proposeEngagement": "Antrag machen",
            "dismiss": "Ablehnen",
            "withdrawProposal": "Antrag zurückziehen",
            "cancelEngagement": "Verlobung auflösen"
        },
        "financing_object": "Finanzierungsobjekt",
        "financing_objects": "Finanzierungsobjekte",
        "borrower": "Kreditnehmer",
        "financing_due_date": "Finanzierung steht bis"
    }
}
</i18n>
